// Life & Wealth Solutions - Phase 5: Notifications Center
// Phoenix Rescue Apps - "Your Agency to AI Today"
// Integrates with Phase 4 Notifications Service

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Bell, Mail, MessageSquare, AlertCircle, CheckCircle,
  Info, XCircle, Trash2, Eye, Settings, Filter,
  Clock, Smartphone, MessageCircle, X
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const NotificationsCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [preferences, setPreferences] = useState(null);
  const [showPreferences, setShowPreferences] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
    fetchStats();
  }, [filter, channelFilter]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          type: filter !== 'all' ? filter : undefined,
          channel: channelFilter !== 'all' ? channelFilter : undefined,
          limit: 50
        }
      };

      const response = await axios.get(`${API_URL}/notifications`, config);
      setNotifications(response.data.notifications || []);
      
      const countsRes = await axios.get(`${API_URL}/notifications/counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCounts(countsRes.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setDemoData();
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(`${API_URL}/notifications/preferences`, config);
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
      setPreferences({
        in_app_enabled: true,
        email_enabled: true,
        sms_enabled: false,
        push_enabled: true,
        quiet_hours_enabled: false,
        quiet_hours_start: '22:00',
        quiet_hours_end: '08:00'
      });
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(`${API_URL}/notifications/stats`, config);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      setStats({
        total_sent: 1247,
        total_delivered: 1189,
        total_read: 876,
        delivery_rate: 95.3,
        read_rate: 73.7
      });
    }
  };

  const setDemoData = () => {
    setNotifications([
      {
        notification_id: 'N1001',
        type: 'success',
        title: 'Payment Received',
        message: 'Payment of $2,500 received from Global Insurance Co',
        channels: ['in_app', 'email'],
        priority: 'high',
        is_read: false,
        created_at: '2025-12-11T10:30:00Z'
      },
      {
        notification_id: 'N1002',
        type: 'info',
        title: 'New Lead Assigned',
        message: 'You have been assigned lead: John Anderson - Term Life Insurance',
        channels: ['in_app', 'push'],
        priority: 'medium',
        is_read: false,
        created_at: '2025-12-11T09:15:00Z'
      },
      {
        notification_id: 'N1003',
        type: 'warning',
        title: 'Appointment Reminder',
        message: 'Meeting with Sarah Martinez in 1 hour - Conference Room A',
        channels: ['in_app', 'email', 'sms'],
        priority: 'high',
        is_read: false,
        created_at: '2025-12-11T08:45:00Z'
      },
      {
        notification_id: 'N1004',
        type: 'alert',
        title: 'High Churn Risk',
        message: 'Customer Metropolitan Health showing signs of churn risk',
        channels: ['in_app', 'email'],
        priority: 'critical',
        is_read: true,
        created_at: '2025-12-10T16:20:00Z'
      },
      {
        notification_id: 'N1005',
        type: 'info',
        title: 'Task Completed',
        message: 'Follow-up call with Michael Chen has been marked as complete',
        channels: ['in_app'],
        priority: 'low',
        is_read: true,
        created_at: '2025-12-10T14:10:00Z'
      },
      {
        notification_id: 'N1006',
        type: 'success',
        title: 'Quote Accepted',
        message: 'Emily Davis accepted your quote for Universal Life Insurance',
        channels: ['in_app', 'email', 'push'],
        priority: 'high',
        is_read: true,
        created_at: '2025-12-10T11:05:00Z'
      }
    ]);

    setUnreadCounts({
      total: 3,
      info: 1,
      success: 1,
      warning: 1,
      alert: 0,
      error: 0
    });
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(`${API_URL}/notifications/${notificationId}/read`, {}, config);
      await fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(`${API_URL}/notifications/read-all`, {}, config);
      await fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.delete(`${API_URL}/notifications/${notificationId}`, config);
      await fetchNotifications();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleUpdatePreferences = async (newPreferences) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(`${API_URL}/notifications/preferences`, newPreferences, config);
      setPreferences(newPreferences);
      setShowPreferences(false);
    } catch (error) {
      console.error('Failed to update preferences:', error);
      alert('Failed to update preferences');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'info': return Info;
      case 'success': return CheckCircle;
      case 'warning': return AlertCircle;
      case 'alert': return AlertCircle;
      case 'error': return XCircle;
      default: return Bell;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'info': return 'text-blue-600 bg-blue-50';
      case 'success': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'alert': return 'text-red-600 bg-red-50';
      case 'error': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'in_app': return Bell;
      case 'email': return Mail;
      case 'sms': return MessageSquare;
      case 'push': return Smartphone;
      default: return MessageCircle;
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter !== 'all' && n.type !== filter) return false;
    if (channelFilter !== 'all' && !n.channels?.includes(channelFilter)) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notifications Center</h1>
          <p className="text-gray-500 mt-1">Manage your multi-channel notifications</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleMarkAllAsRead}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
          >
            <CheckCircle className="h-4 w-4" />
            <span>Mark All Read</span>
          </button>
          <button
            onClick={() => setShowPreferences(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Settings className="h-4 w-4" />
            <span>Preferences</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Unread</p>
              <p className="text-2xl font-bold text-blue-600">{unreadCounts.total || 0}</p>
            </div>
            <Bell className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Sent</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total_sent || 0}</p>
            </div>
            <MessageCircle className="h-8 w-8 text-gray-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Delivered</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total_delivered || 0}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Delivery Rate</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.delivery_rate || 0}%</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Read Rate</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.read_rate || 0}%</p>
            </div>
            <Eye className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Type:</span>
            {['all', 'info', 'success', 'warning', 'alert', 'error'].map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${
                  filter === type
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type}
                {type !== 'all' && unreadCounts[type] > 0 && (
                  <span className="ml-1 text-xs">({unreadCounts[type]})</span>
                )}
              </button>
            ))}
          </div>
          <div className="flex items-center space-x-2 ml-auto">
            <span className="text-sm font-medium text-gray-700">Channel:</span>
            {['all', 'in_app', 'email', 'sms', 'push'].map((channel) => (
              <button
                key={channel}
                onClick={() => setChannelFilter(channel)}
                className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${
                  channelFilter === channel
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {channel === 'in_app' ? 'In-App' : channel}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
            <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
            <p className="text-gray-500">You're all caught up!</p>
          </div>
        ) : (
          filteredNotifications.map((notification) => {
            const TypeIcon = getTypeIcon(notification.type);
            return (
              <div
                key={notification.notification_id}
                className={`bg-white rounded-lg shadow border-l-4 p-4 hover:shadow-lg transition-shadow ${
                  notification.is_read ? 'border-l-gray-300 opacity-75' : 'border-l-blue-600'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className={`p-3 rounded-lg ${getTypeColor(notification.type)}`}>
                      <TypeIcon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {notification.title}
                        </h3>
                        {!notification.is_read && (
                          <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                        )}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(notification.priority)}`}>
                          {notification.priority}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">{notification.message}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4" />
                          <span>{formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {notification.channels?.map((channel) => {
                            const ChannelIcon = getChannelIcon(channel);
                            return (
                              <div
                                key={channel}
                                className="flex items-center space-x-1 text-gray-400"
                                title={channel}
                              >
                                <ChannelIcon className="h-4 w-4" />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {!notification.is_read && (
                      <button
                        onClick={() => handleMarkAsRead(notification.notification_id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Mark as read"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(notification.notification_id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Preferences Modal */}
      {showPreferences && preferences && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Notification Preferences</h2>
              <button
                onClick={() => setShowPreferences(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Channel Toggles */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Channels</h3>
                <div className="space-y-3">
                  {[
                    { key: 'in_app_enabled', label: 'In-App Notifications', icon: Bell },
                    { key: 'email_enabled', label: 'Email Notifications', icon: Mail },
                    { key: 'sms_enabled', label: 'SMS Notifications', icon: MessageSquare },
                    { key: 'push_enabled', label: 'Push Notifications', icon: Smartphone }
                  ].map(({ key, label, icon: Icon }) => (
                    <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Icon className="h-5 w-5 text-gray-600" />
                        <span className="font-medium text-gray-900">{label}</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences[key]}
                          onChange={(e) => setPreferences({ ...preferences, [key]: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quiet Hours */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Quiet Hours</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-900">Enable Quiet Hours</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.quiet_hours_enabled}
                        onChange={(e) => setPreferences({ ...preferences, quiet_hours_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  {preferences.quiet_hours_enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                        <input
                          type="time"
                          value={preferences.quiet_hours_start}
                          onChange={(e) => setPreferences({ ...preferences, quiet_hours_start: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                        <input
                          type="time"
                          value={preferences.quiet_hours_end}
                          onChange={(e) => setPreferences({ ...preferences, quiet_hours_end: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowPreferences(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdatePreferences(preferences)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsCenter;
